import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../stores";
import { getSubscriptionDetailsAction } from "../../features/subscription-module/subscriptionAction";

const Card = styled(Box)(({ theme }) => ({
  backgroundColor: "#f8f8f8",
  borderRadius: "8px",
  padding: theme.spacing(2),
  width: "100%",
  maxWidth: "auto",
  minHeight: 380,
  margin: theme.spacing(2, "auto"),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
    maxWidth: "100%",
    margin: theme.spacing(1, 0),
    minHeight: 280,
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: "20px",
  fontWeight: 600,
  color: "#4b5b5d",
  textAlign: "left",
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    fontSize: "18px",
  },
}));

const PlanNameLabelChip = styled(Chip)(({ theme }) => ({
  backgroundColor: "#f2efe9",
  color: "#455354",
  fontWeight: 600,
  borderRadius: 4,
  padding: theme.spacing(1),
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
  fontSize: "11px",
  alignSelf: "flex-start",
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
    height: "24px",
    padding: theme.spacing(0.35),
  },
}));

const QuotaChip = styled(Chip)(({ theme }) => ({
  backgroundColor: "#fff",
  color: "#455354",
  fontWeight: 500,
  borderRadius: 4,
  padding: theme.spacing(1),
  marginBottom: theme.spacing(1),
  fontSize: "10px",
  alignSelf: "flex-start",
  [theme.breakpoints.down("sm")]: {
    fontSize: "11px",
    height: "22px",
    padding: theme.spacing(0.25),
  },
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  color: "#757575",
  fontSize: "12px",
  textAlign: "left",
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
    lineHeight: 1.4,
  },
}));

const PriceText = styled(Typography)(({ theme }) => ({
  fontSize: "22px",
  fontWeight: 600,
  color: "#4b5b5d",
  textAlign: "left",
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    fontSize: "20px",
  },
}));

const FeatureItem = styled(Typography)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  fontSize: 12,
  color: "#3f3f3f",
  flexWrap: "nowrap",
  marginBottom: theme.spacing(1),
  "&::before": {
    content: '"✓"',
    color: "#4caf50",
    fontWeight: 600,
    fontSize: 13,
    marginRight: theme.spacing(1.1),
    marginTop: 1,
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "13px",
    "&::before": {
      fontSize: 11,
      marginRight: theme.spacing(0.8),
    },
  },
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: "#d32f2f",
  fontSize: "14px",
  fontWeight: 500,
  textAlign: "center",
  marginTop: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
  },
}));

// Loader Container
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "360px",
  width: "100%",
  height: "100%",
}));

const LoaderText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
}));

const CurrentSubscriptionPlanCard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch<AppDispatch>();
  const { subscriptionDetails, loading, error } = useSelector(
    (state: RootState) => state.subscription
  );

  useEffect(() => {
    dispatch(getSubscriptionDetailsAction() as any);
  }, [dispatch]);

  const isNoSubscription = error && error.includes("Subscription not found");

  // Calculate usage percentages
  const calculateUsagePercentage = (used: number, limit: number): number => {
    if (limit === 0) return 0;
    return Math.min(Math.round((used / limit) * 100), 100);
  };

  const siteUsagePercentage = subscriptionDetails
    ? calculateUsagePercentage(
        subscriptionDetails.siteQuotaUsed,
        subscriptionDetails.siteQuotaLimit
      )
    : 0;

  const fleetUsagePercentage = subscriptionDetails
    ? calculateUsagePercentage(
        subscriptionDetails.fleetQuotaUsed,
        subscriptionDetails.fleetQuotaLimit
      )
    : 0;

  const plan = {
    name: isNoSubscription ? "N/A" : subscriptionDetails?.planName || "N/A",
    description: isNoSubscription
      ? "N/A"
      : subscriptionDetails?.planDescription ||
        "No subscription details available",
    price: isNoSubscription
      ? "N/A"
      : subscriptionDetails?.planPrice != null
      ? `$${subscriptionDetails.planPrice.toFixed(2)}/month`
      : "N/A",
    features: isNoSubscription
      ? ["N/A"]
      : subscriptionDetails?.planFeatures || [],

    // Site quota data
    siteQuotaUsed: isNoSubscription
      ? 0
      : subscriptionDetails?.siteQuotaUsed || 0,
    siteQuotaLimit: isNoSubscription
      ? 0
      : subscriptionDetails?.siteQuotaLimit || 0,
    siteUsagePercentage,

    // Fleet quota data
    fleetQuotaUsed: isNoSubscription
      ? 0
      : subscriptionDetails?.fleetQuotaUsed || 0,
    fleetQuotaLimit: isNoSubscription
      ? 0
      : subscriptionDetails?.fleetQuotaLimit || 0,
    fleetUsagePercentage,
  };

  const hasQuotaData = plan.siteQuotaLimit > 0 || plan.fleetQuotaLimit > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <Card>
        <Title>Current Subscription Plan</Title>
        {loading ? (
          <LoaderContainer>
            <CircularProgress
              size={40}
              thickness={4}
              sx={{
                color: "#4b5b5d",
              }}
            />
            <LoaderText>Loading subscription details...</LoaderText>
          </LoaderContainer>
        ) : error && !isNoSubscription ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: isMobile ? "flex-start" : "space-between",
                alignItems: isMobile ? "flex-start" : "center",
                mb: 1,
                gap: isMobile ? theme.spacing(1) : 0,
              }}
            >
              <PlanNameLabelChip label={plan.name} />

              {/* Conditionally render quota chips */}
              {hasQuotaData && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  {plan.siteQuotaLimit > 0 && (
                    <QuotaChip
                      label={`${plan.siteQuotaUsed}/${plan.siteQuotaLimit} Sites Report`}
                    />
                  )}
                  {plan.fleetQuotaLimit >= 0 && (
                    <QuotaChip
                      label={`${plan.fleetQuotaUsed}/${plan.fleetQuotaLimit} Fleets Report`}
                    />
                  )}
                </Box>
              )}
            </Box>

            <SubTitle>{plan.description}</SubTitle>
            <PriceText>{plan.price}</PriceText>

            <Divider sx={{ mb: 2 }} />

            {plan.features.length > 0 ? (
              plan.features.map((feature, idx) => (
                <FeatureItem key={idx}>{feature}</FeatureItem>
              ))
            ) : (
              <FeatureItem>No features available</FeatureItem>
            )}
          </>
        )}
      </Card>
    </motion.div>
  );
};

export default CurrentSubscriptionPlanCard;
